import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, Grid } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBullseye, faCloudRain, faSun } from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
import { getExecutiveSummary, getExecutiveReadyToSell, getExecutiveSales } from './services/api'
import { getBranchList, getBudgetYearsList, getCatalogsList, getTypesList } from '../dashboard/services/api'
import SummaryDonut from './summaryDonut'
import ProductionChart from './productionChart'
import ComparisonBarChart from './comparisonBarChart'
import StackedBarChart from './stackedBarChart'
import SalePriceTable from '../dashboard/salePriceTable'
import ReadyForSale from '../dashboard/readyForSale'
import DashboardSkeleton from '../../component/DashboardSkeleton'
import { FilterBar, YearSelect, MultiSelectFilter, QuarterSelect, UnitSelect } from './filterControls'
import LoadingOverlay from '../../component/LoadingOverlay'
import useDebouncedResource from '../../component/useDebouncedResource'
import '../dashboard/dashboard.scss'
import './executiveDashboard.scss'

const toTon = (value) => Math.round((+value || 0) / 1000)
const seasons = [{ value: 'แล้ง' }, { value: 'ฝน' }]

// pivot rows [{ BranchName, CatalogName, Qty }] → stacked { categories(ศูนย์ เรียงมาก→น้อย), series(รายพันธุ์) }
function pivotBranchCatalog(rows) {
    const list = rows || []
    const branchTotals = {}
    list.forEach(r => { branchTotals[r.BranchName] = (branchTotals[r.BranchName] || 0) + r.Qty })
    const branches = Object.keys(branchTotals).sort((a, b) => branchTotals[b] - branchTotals[a])
    const catalogs = [...new Set(list.map(r => r.CatalogName))]
    const series = catalogs.map(cat => ({
        name: cat,
        data: branches.map(br => {
            const found = list.find(r => r.BranchName === br && r.CatalogName === cat)
            return found ? found.Qty : 0
        })
    }))
    return { categories: branches, series }
}

export default function ExecutiveDashboard() {
    const { profile } = useSelector(state => state.profile)
    const canRead = profile?.Permissions?.find((x) => x?.Action === 'ExecutiveDashboard.Read')?.Permit

    const defaultYear = dayjs(new Date()).add(3, 'month').format('BBBB')

    // lookups สำหรับตัวกรอง
    const [catalogs, setCatalogs] = useState([])
    const [branchs, setBranchs] = useState([])   // ยกเว้นศูนย์ 00
    const [budgetYears, setBudgetYears] = useState([])
    const [types, setTypes] = useState([])

    // ตัวกรอง — กลุ่มการผลิต (ปีงบ + ศูนย์)
    const [prodYear, setProdYear] = useState(defaultYear)
    const [prodBranchNos, setProdBranchNos] = useState([])

    // ตัวกรอง — กราฟพร้อมจำหน่าย รายศูนย์×พันธุ์ (ศูนย์ + พันธุ์)
    const [rtsBranchNos, setRtsBranchNos] = useState([])
    const [rtsCatalogNos, setRtsCatalogNos] = useState([])

    // ตัวกรอง — กราฟยอดจำหน่ายสะสม (ปีงบ + ไตรมาส + ศูนย์ + พันธุ์ + หน่วย)
    const [salesYear, setSalesYear] = useState(defaultYear)
    const [salesQuarter, setSalesQuarter] = useState('')   // '' = ทุกไตรมาส
    const [salesBranchNos, setSalesBranchNos] = useState([])
    const [salesCatalogNos, setSalesCatalogNos] = useState([])
    const [salesUnit, setSalesUnit] = useState('baht')   // หน่วยเริ่มต้น = บาท

    const enabled = !!(profile && canRead)

    // โหลด lookups ครั้งเดียว
    useEffect(() => {
        if (!enabled) return
        const load = async () => {
            const [c, b, y, t] = await Promise.all([
                getCatalogsList(), getBranchList(), getBudgetYearsList(), getTypesList()
            ])
            setCatalogs(c || [])
            setBranchs((b || []).filter(x => x?.BranchNo !== '00'))
            setBudgetYears(y || [])
            setTypes(t || [])
        }
        load()
    }, [enabled])

    // executive-summary — debounce + stale-guard เมื่อเปลี่ยนตัวกรองกลุ่มการผลิต
    const summary = useDebouncedResource(
        () => getExecutiveSummary({ BranchNo: prodBranchNos, BudgetYear: [prodYear] }),
        JSON.stringify([prodYear, prodBranchNos]), enabled
    )
    const data = summary.data

    // ready-to-sell — debounce + stale-guard เมื่อเปลี่ยนตัวกรองศูนย์/พันธุ์
    const rts = useDebouncedResource(
        () => getExecutiveReadyToSell({ BranchNo: rtsBranchNos, CatalogNo: rtsCatalogNos }),
        JSON.stringify([rtsBranchNos, rtsCatalogNos]), enabled
    )
    const readyToSell = rts.data

    // sales — debounce + stale-guard เมื่อเปลี่ยนตัวกรองปีงบ/ไตรมาส/ศูนย์/พันธุ์
    const salesRes = useDebouncedResource(
        () => getExecutiveSales({ BranchNo: salesBranchNos, CatalogNo: salesCatalogNos, BudgetYear: [salesYear], Quarter: salesQuarter ? [salesQuarter] : [], Unit: [salesUnit] }),
        JSON.stringify([salesYear, salesQuarter, salesBranchNos, salesCatalogNos, salesUnit]), enabled
    )
    const sales = salesRes.data

    // fail-closed: รอ profile โหลดเสร็จก่อน แสดงเนื้อหาเฉพาะผู้มีสิทธิ์เท่านั้น
    if (!profile) {
        return <DashboardSkeleton />
    }

    if (!canRead) {
        return (
            <div className="container-fluid executive-dashboard">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <div className="card-content-ed text-center">
                            <h5>ไม่มีสิทธิ์เข้าถึงหน้านี้</h5>
                            <div>กรุณาติดต่อผู้ดูแลระบบเพื่อขอสิทธิ์การใช้งาน Dashboard ผู้บริหาร</div>
                        </div>
                    </Grid>
                </Grid>
            </div>
        )
    }

    // แสดง skeleton เต็มหน้า "เฉพาะโหลดครั้งแรก" (สรุป + พร้อมจำหน่าย); refetch จากตัวกรองใช้ overlay แทน
    if (!summary.loadedOnce || !rts.loadedOnce) {
        return <DashboardSkeleton />
    }

    if (!data) {
        return (
            <div className="container-fluid executive-dashboard">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <div className="card-content-ed text-center">
                            <h5>ไม่สามารถโหลดข้อมูลได้</h5>
                            <div>กรุณาลองใหม่อีกครั้ง หรือติดต่อผู้ดูแลระบบ</div>
                        </div>
                    </Grid>
                </Grid>
            </div>
        )
    }

    // มุม 2: พันธุ์ × ชั้นพันธุ์ (3 ชั้น) — เรียงยอดรวมน้อยไปมาก
    const rtsCatalogClass = () => {
        const rows = [...(readyToSell?.ByCatalogClass || [])]
            .sort((a, b) => (a.Main + a.Extend + a.Distribute) - (b.Main + b.Extend + b.Distribute))
        return {
            categories: rows.map(r => r.CatalogName),
            series: [
                { name: 'ชั้นพันธุ์หลัก', data: rows.map(r => r.Main), color: '#A9D18E' },
                { name: 'ชั้นพันธุ์ขยาย', data: rows.map(r => r.Extend), color: '#2E75B6' },
                { name: 'ชั้นพันธุ์จำหน่าย', data: rows.map(r => r.Distribute), color: '#ED7D31' },
            ]
        }
    }

    // มุม 3: กลุ่มข้าว × ฤดู — เรียงยอดรวมน้อยไปมาก
    const rtsGroupSeason = () => {
        const rows = [...(readyToSell?.ByGroupSeason || [])]
            .sort((a, b) => (a.Rainy + a.Dry) - (b.Rainy + b.Dry))
        return {
            categories: rows.map(r => r.GroupName),
            series: [
                { name: 'ฤดูฝน', data: rows.map(r => r.Rainy), color: '#2E75B6' },
                { name: 'ฤดูแล้ง', data: rows.map(r => r.Dry), color: '#FFC000' },
            ]
        }
    }

    const view1 = pivotBranchCatalog(readyToSell?.ByBranchCatalog)
    const view2 = rtsCatalogClass()
    const view3 = rtsGroupSeason()
    const salesView = pivotBranchCatalog(sales?.ByBranchCatalog)

    // ข้อมูลกราฟเทียบเป้าหมาย vs เมล็ดดี (ใช้ชื่อศูนย์แบบเต็ม)
    const byGroupItems = (data?.ByItemGroup || []).map(x => ({ Name: x.GroupName, Target: x.Target, Actual: x.Actual }))
    const byBranchItems = (data?.ByBranch || []).map(x => ({ Name: x.BranchName, Target: x.Target, Actual: x.Actual }))
    const byCatalogItems = (data?.ByCatalog || []).map(x => ({ Name: x.CatalogName, Target: x.Target, Actual: x.Actual }))

    // ความสูงเท่ากันต่อบรรทัด (ยึดจำนวนแท่งที่มากกว่าในคู่)
    const branchCatalogRowHeight = Math.max(byBranchItems.length, byCatalogItems.length, 8) * 26
    const readyRowHeight = Math.max(view2.categories.length, view3.categories.length, 6) * 32
    const view1Height = Math.max(520, view1.categories.length * 26)
    const salesHeight = Math.max(520, salesView.categories.length * 26)

    return (
        <div className="container-fluid executive-dashboard">
            <Grid container spacing={2}>
                {/* ======== กลุ่มการผลิต (ครอบกรอบ + ตัวกรอง ปีงบ/ศูนย์) ======== */}
                <Grid item xs={12}>
                    <div className="exec-section">
                        <Box sx={{ display: 'flex', flexDirection: { md: 'row', xs: 'column' }, justifyContent: 'space-between', alignItems: { md: 'center', xs: 'stretch' }, marginBottom: '10px' }}>
                            <h2 className='dash-title'>ภาพรวมการผลิตเมล็ดพันธุ์ข้าว</h2>
                            <FilterBar>
                                <YearSelect budgetYears={budgetYears} value={prodYear} onChange={setProdYear} />
                                <MultiSelectFilter placeholder="ศูนย์" allLabel="ศูนย์ทั้งหมด" options={branchs} idKey="BranchNo" nameKey="BranchName" selectedIds={prodBranchNos} onChange={setProdBranchNos} />
                            </FilterBar>
                        </Box>
                        <LoadingOverlay loading={summary.loading}>
                        <Grid container spacing={2}>
                            <Grid item md={3} sm={6} xs={12}>
                                <div className="exec-card">
                                    <div className="exec-card-icon icon-target">
                                        <FontAwesomeIcon icon={faBullseye} />
                                    </div>
                                    <div>
                                        <div className="exec-card-title">เป้าหมายรวม</div>
                                        <div className="exec-card-value">{(toTon(data?.RainyTarget) + toTon(data?.DryTarget))?.toLocaleString()}</div>
                                        <div className="exec-card-sub">ตัน</div>
                                    </div>
                                </div>
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <div className="exec-card">
                                    <div className="exec-card-icon icon-rainy">
                                        <FontAwesomeIcon icon={faCloudRain} />
                                    </div>
                                    <div>
                                        <div className="exec-card-title">ฤดูฝน {data?.RainyYear}</div>
                                        <div className="exec-card-sub">แผน/ผลดำเนินการ (ตัน)</div>
                                        <div className="exec-card-value">{toTon(data?.RainyTarget)?.toLocaleString()}/{toTon(data?.RainyActual)?.toLocaleString()}</div>
                                    </div>
                                </div>
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <div className="exec-card">
                                    <div className="exec-card-icon icon-dry">
                                        <FontAwesomeIcon icon={faSun} />
                                    </div>
                                    <div>
                                        <div className="exec-card-title">ฤดูแล้ง {data?.DryYear}</div>
                                        <div className="exec-card-sub">แผน/ผลดำเนินการ (ตัน)</div>
                                        <div className="exec-card-value">{toTon(data?.DryTarget)?.toLocaleString()}/{toTon(data?.DryActual)?.toLocaleString()}</div>
                                    </div>
                                </div>
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <div className="exec-card exec-card-donut">
                                    <SummaryDonut data={data} />
                                </div>
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <div className="card-content-ed">
                                    <h5 className="exec-chart-title">ผลการผลิต</h5>
                                    <ProductionChart data={data} height={400} />
                                </div>
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <div className="card-content-ed">
                                    <h5 className="exec-chart-title">ผลดำเนินการผลิต ตามกลุ่มข้าว (ตัน)</h5>
                                    <ComparisonBarChart items={byGroupItems} height={400} />
                                </div>
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <div className="card-content-ed">
                                    <h5 className="exec-chart-title">ผลดำเนินการผลิต ตามศูนย์เมล็ดพันธุ์ข้าว (ตัน)</h5>
                                    <ComparisonBarChart items={byBranchItems} height={branchCatalogRowHeight} />
                                </div>
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <div className="card-content-ed">
                                    <h5 className="exec-chart-title">ผลดำเนินการผลิต ตามพันธุ์ข้าว (ตัน)</h5>
                                    <ComparisonBarChart items={byCatalogItems} height={branchCatalogRowHeight} />
                                </div>
                            </Grid>
                        </Grid>
                        </LoadingOverlay>
                    </div>
                </Grid>

                {/* ======== ราคาขายเมล็ดพันธุ์ดี ======== */}
                <Grid item xs={12}>
                    <div className="card-content-ed">
                        <SalePriceTable />
                    </div>
                </Grid>

                {/* ======== ตารางเมล็ดพันธุ์ข้าวพร้อมจำหน่าย (ลอกจากหน้าเจ้าหน้าที่) ======== */}
                <Grid item xs={12}>
                    <div className="card-content-ed">
                        <ReadyForSale branchs={branchs} types={types} catalogs={catalogs} budgetYears={budgetYears} seasons={seasons} />
                    </div>
                </Grid>

                {/* ======== กลุ่มเมล็ดพันธุ์ดีพร้อมจำหน่าย (ครอบกรอบ + ตัวกรอง ศูนย์/พันธุ์ ที่ header) ======== */}
                <Grid item xs={12}>
                    <div className="exec-section">
                        <Box sx={{ display: 'flex', flexDirection: { md: 'row', xs: 'column' }, justifyContent: 'space-between', alignItems: { md: 'center', xs: 'stretch' }, marginBottom: '10px' }}>
                            <h2 className='dash-title'>เมล็ดพันธุ์ดีพร้อมจำหน่าย</h2>
                            <FilterBar>
                                <MultiSelectFilter placeholder="ศูนย์" allLabel="ศูนย์ทั้งหมด" options={branchs} idKey="BranchNo" nameKey="BranchName" selectedIds={rtsBranchNos} onChange={setRtsBranchNos} />
                                <MultiSelectFilter placeholder="พันธุ์" allLabel="พันธุ์ทั้งหมด" options={catalogs} idKey="CatalogNo" nameKey="CatalogName" selectedIds={rtsCatalogNos} onChange={setRtsCatalogNos} />
                            </FilterBar>
                        </Box>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <div className="card-content-ed">
                                    <h5 className="exec-chart-title">เมล็ดพันธุ์ดีพร้อมจำหน่าย (หักยอดสำรอง) รายศูนย์ × พันธุ์</h5>
                                    <LoadingOverlay loading={rts.loading}>
                                        <StackedBarChart
                                            categories={view1.categories}
                                            series={view1.series}
                                            horizontal={true}
                                            height={view1Height}
                                            showLegend={false}
                                        />
                                    </LoadingOverlay>
                                </div>
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <div className="card-content-ed">
                                    <h5 className="exec-chart-title">เมล็ดพันธุ์ดีพร้อมจำหน่าย รายพันธุ์ × ชั้นพันธุ์</h5>
                                    <LoadingOverlay loading={rts.loading}>
                                        <StackedBarChart
                                            categories={view2.categories}
                                            series={view2.series}
                                            horizontal={true}
                                            height={readyRowHeight}
                                            showLegend={true}
                                        />
                                    </LoadingOverlay>
                                </div>
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <div className="card-content-ed">
                                    <h5 className="exec-chart-title">เมล็ดพันธุ์ดีพร้อมจำหน่าย รายกลุ่มข้าว × ฤดู</h5>
                                    <LoadingOverlay loading={rts.loading}>
                                        <StackedBarChart
                                            categories={view3.categories}
                                            series={view3.series}
                                            horizontal={true}
                                            height={readyRowHeight}
                                            showLegend={true}
                                            showDataLabels={true}
                                        />
                                    </LoadingOverlay>
                                </div>
                            </Grid>
                        </Grid>
                    </div>
                </Grid>

                {/* ======== ยอดจำหน่ายสะสม รายศูนย์×พันธุ์ (ตัวกรอง ปีงบ/ศูนย์/พันธุ์) ======== */}
                <Grid item xs={12}>
                    <div className="card-content-ed">
                        {/* ชื่อกราฟ อยู่ตรงกลาง (exec-chart-title = text-align center) */}
                        <h5 className="exec-chart-title" style={{ marginBottom: '10px' }}>
                            ยอดจำหน่ายสะสม ปี {salesYear} รายศูนย์ × พันธุ์ ({salesUnit === 'kg' ? 'กิโลกรัม' : 'บาท'})
                        </h5>
                        {/* บรรทัดตัวเลือก: หน่วยชิดซ้าย + ตัวกรองชิดขวา; จอเล็ก (xs) เรียงลงเป็นคอลัมน์ ไม่ซ้อนกัน */}
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { md: 'row', xs: 'column' },
                            justifyContent: 'space-between',
                            alignItems: { md: 'center', xs: 'stretch' },
                            flexWrap: 'wrap',
                            gap: '10px',
                            marginBottom: '10px',
                            '& .backgroundField': { background: '#a2c4e0' },
                        }}>
                            {/* หน่วย ชิดซ้าย */}
                            <Box sx={{ display: 'flex', flexShrink: 0 }}>
                                <UnitSelect value={salesUnit} onChange={setSalesUnit} />
                            </Box>
                            {/* ตัวกรอง ชิดขวา */}
                            <FilterBar>
                                <YearSelect budgetYears={budgetYears} value={salesYear} onChange={setSalesYear} />
                                <QuarterSelect value={salesQuarter} onChange={setSalesQuarter} />
                                <MultiSelectFilter placeholder="ศูนย์" allLabel="ศูนย์ทั้งหมด" options={branchs} idKey="BranchNo" nameKey="BranchName" selectedIds={salesBranchNos} onChange={setSalesBranchNos} />
                                <MultiSelectFilter placeholder="พันธุ์" allLabel="พันธุ์ทั้งหมด" options={catalogs} idKey="CatalogNo" nameKey="CatalogName" selectedIds={salesCatalogNos} onChange={setSalesCatalogNos} />
                            </FilterBar>
                        </Box>
                        <LoadingOverlay loading={salesRes.loading}>
                            <StackedBarChart
                                categories={salesView.categories}
                                series={salesView.series}
                                horizontal={true}
                                height={salesHeight}
                                showLegend={false}
                                unit={salesUnit === 'kg' ? 'กิโลกรัม' : 'บาท'}
                            />
                        </LoadingOverlay>
                    </div>
                </Grid>
            </Grid>
        </div>
    )
}
